import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';
import Login from './pages/Login';
import { AdminRoutes } from 'routes/AdminRoutes';
import { StudentRoutes } from 'routes/StudentRoutes';
import { InstructorRoutes } from 'routes/InstructorRoutes';
import { ExaminerRoutes } from 'routes/ExaminerRoutes';
import { CorporateRoutes } from 'routes/CorporateRoutes';
import { useTranslation } from 'react-i18next';
import { Toaster } from "react-hot-toast";
import { StudentProvider } from "context/StudentContext"; 
import Auth from './pages/Login/Auth';
import apiRequest from 'services/axois';
const StudentRegisterWizard = lazy(() =>
  import('./components/wizard/pages/Student'),
);
const MedicalRegisterWizard = lazy(() =>
  import('./components/wizard/pages/Medical'),
);
const TrainingRegisterWizard = lazy(() =>
  import('./components/wizard/pages/Training'),
);
const ProductsList = lazy(() => import('./pages/Products/List'));
const ProductsDetails = lazy(() => import('./pages/Products/Details'));
const ProductPayment = lazy(() => import('./pages/Products/Payment'));
const InvoicePrint = lazy(() =>
  import("InvoicePrint")
);
const CertificatePage = lazy(() => import('./pages/Certificate'));
const SESSION_CHECK_INTERVAL_MS = 30 * 1000;
const ACTIVITY_TOUCH_MIN_MS = 60 * 1000;
function App() {
  const { i18n } = useTranslation();
  let lang = localStorage.getItem("lang") || 'en';
  const isRTL = i18n.language === 'en' ? 'ltr' : 'rtl';

  useEffect(() => {
    i18n.changeLanguage(lang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const readToken = () => {
      const raw = localStorage.getItem("user");
      let token = null;
      try {
        const parsed = JSON.parse(raw);
        token = parsed?.token || parsed?.accessToken || parsed?.access_token || parsed;
      } catch {
        token = raw;
      }
      return String(token || "")
        .trim()
        .replace(/^Bearer\s+/i, "")
        .replace(/^"+|"+$/g, "");
    };

    const runSessionCheck = async () => {
      const token = readToken();
      const userIdRaw = localStorage.getItem("userId");
      const userId = Number(userIdRaw || 0);

      if (!token || !userId) return;

      try {
        await apiRequest(
          "post",
          "/auth/test-token",
          { token, userId, touch: false },
          { suppressErrorToast: true },
        );
      } catch {
        // 401 is handled centrally by apiRequest and axios interceptors.
      }
    };

    let lastActivityTouchAt = 0;
    const touchActivity = async () => {
      const now = Date.now();
      if (now - lastActivityTouchAt < ACTIVITY_TOUCH_MIN_MS) return;

      const token = readToken();
      const userIdRaw = localStorage.getItem("userId");
      const userId = Number(userIdRaw || 0);
      if (!token || !userId) return;

      lastActivityTouchAt = now;
      try {
        await apiRequest(
          "post",
          "/auth/test-token",
          { token, userId, touch: true },
          { suppressErrorToast: true },
        );
      } catch {
        // 401 is handled centrally by apiRequest and axios interceptors.
      }
    };

    runSessionCheck();
    const intervalId = window.setInterval(runSessionCheck, SESSION_CHECK_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") runSessionCheck();
    };
    const onUserActivity = () => {
      touchActivity();
    };

    const activityEvents = [
      "mousedown",
      "keydown",
      "touchstart",
      "scroll",
      "click",
    ];

    document.addEventListener("visibilitychange", onVisible);
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, onUserActivity, { passive: true });
    });

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, onUserActivity);
      });
    };
  }, []);

  return (
    <div className={isRTL ? "rtl" : "ltr"} lang={i18n.language}>
      <Toaster position="top-center" />
      <Suspense
        fallback={
          <div className="text-lg font-bold m-6 animate-bounce">
            {i18n.language === "en" ? "Loading..." : "جار تحميل البيانات"}
          </div>
        }
      >
        <BrowserRouter
          future={{
            // Opt-in to the new startTransition behavior
            v7_startTransition: true,
            // Opt-in to the new relative splat path behavior
            v7_relativeSplatPath: true,
          }}
        >
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Login />} />
            <Route path="/Auth/:token" element={<Auth />} />
            <Route path="/invoice/:id/print" element={<InvoicePrint />} />
            <Route
              path="/student/*"
              element={
                <StudentProvider>
                  <StudentRoutes />
                </StudentProvider>
              }
            />
            <Route path="/certificate/:payload" element={<CertificatePage />} />
            <Route
              path="/student-register"
              element={<StudentRegisterWizard />}
            />
            <Route
              path="/training-register"
              element={<TrainingRegisterWizard />}
            />
            <Route
              path="/medical-register"
              element={<MedicalRegisterWizard />}
            />
            <Route path="/admin/*" element={<AdminRoutes />} />
            <Route path="/student/*" element={<StudentRoutes />} />
            <Route path="/instructor/*" element={<InstructorRoutes />} />
            <Route path="/examiner/*" element={<ExaminerRoutes />} />
            <Route path="/corporate/*" element={<CorporateRoutes />} />
            <Route path="products" element={<ProductsList />} />
            <Route path="product-details/:id" element={<ProductsDetails />} />
            <Route path="product-payment/:id" element={<ProductPayment />} />
            <Route path="/" element={<Login />} />
            <Route path="*" element={<Login />} />
          </Routes>
        </BrowserRouter>
      </Suspense>
    </div>
  );
}
export default App;
